import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Search, X, BookOpen, Heart, Sparkles, ChevronRight, Link2, Check, Printer } from 'lucide-react';
import { wellnessResources } from '../data/resources';
import { wellnessContent } from '../data/wellnessContent';
import NewsletterSignup from '../components/NewsletterSignup';
import useNewsletterPrompt from '../hooks/useNewsletterPrompt';
import { useResourceDeepLink } from '../hooks/useResourceDeepLink';

// Custom logo for section icons
const SECTION_ICON = 'https://customer-assets.emergentagent.com/job_hello-world-4084/artifacts/vnl8646a_Grounded%20Wellness%20%28Logo%29%20%284%29.png';

// Helper component to render icon - either styled circle or emoji
const SectionIcon = ({ icon, size = 24 }) => {
  if (icon === 'logo') {
    return (
      <span 
        className="inline-block rounded-full bg-terracotta border-2 border-terracotta-dark shadow-sm"
        style={{ 
          width: size * 0.5, 
          height: size * 0.5,
          minWidth: size * 0.5,
          minHeight: size * 0.5
        }}
      />
    );
  }
  return <span style={{ fontSize: size }}>{icon}</span>;
};

const WellnessResourcesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  
  // Newsletter soft prompt
  const { showPrompt, dismissPrompt } = useNewsletterPrompt('wellness-resources');

  const getDetailedContent = (resourceId) => {
    return wellnessContent[resourceId] || null;
  };

  // Deep linking
  const { setResourceParam, clearResourceParam, copyResourceLink, copied } = useResourceDeepLink({
    resources: wellnessResources,
    getDetailedContent,
    onSelect: setSelectedResource,
  });

  const filteredResources = wellnessResources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTypeColor = (type) => {
    if (type === 'PDF Guide') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (type === 'Coming Soon') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-rose-100 text-rose-700 border-rose-200';
  };

  const handleResourceClick = (resource) => {
    const detailed = getDetailedContent(resource.id);
    if (detailed) {
      setSelectedResource({ ...resource, detailed });
      setResourceParam(resource.id);
    } else if (resource.downloadUrl) {
      window.open(resource.downloadUrl, '_blank');
    }
  };

  const handleCloseResource = () => {
    setSelectedResource(null);
    clearResourceParam();
  };

  return (
    <div className="bg-cream pt-24 min-h-screen" data-testid="wellness-resources-page">
      {/* Header */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/resources"
              className="flex items-center gap-2 text-stone-500 hover:text-forest font-body mb-6 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Resources
            </Link>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <div>
                <h1 className="font-heading text-4xl md:text-5xl text-forest">
                  Wellness Resources
                </h1>
                <p className="text-stone-500 font-body mt-2 max-w-xl">
                  Guides for sleep, stress, digestion, and holistic approaches to restoring vitality.
                </p>
              </div>

              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-stone-300 rounded-full pl-11 pr-4 py-2.5 text-forest text-sm placeholder:text-stone-400 focus:outline-none focus:border-terracotta"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => {
              const hasDetailedContent = !!getDetailedContent(resource.id);
              return (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => handleResourceClick(resource)}
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-all duration-500 h-full flex flex-col">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={resource.image}
                        alt={resource.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full font-body text-xs border ${getTypeColor(resource.type)}`}>
                          {resource.type}
                        </span>
                      </div>
                      {(hasDetailedContent || resource.downloadUrl) && (
                        <div className="absolute inset-0 bg-forest/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <span className="bg-white text-forest px-4 py-2 rounded-full font-body text-sm font-medium">
                            {hasDetailedContent ? 'View Resource' : 'Download Guide'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-heading text-xl text-forest mb-2 group-hover:text-terracotta transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-stone-500 font-body text-sm mb-4 flex-1">
                        {resource.description}
                      </p>

                      {/* Topics */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {resource.topics.slice(0, 3).map((topic, i) => (
                          <span key={i} className="px-2 py-1 bg-stone-100 text-stone-600 rounded-full text-xs font-body">
                            {topic}
                          </span>
                        ))}
                      </div>

                      {/* Action */}
                      <div className="flex items-center gap-2 text-terracotta font-body text-sm font-medium">
                        {hasDetailedContent ? (
                          <>
                            <BookOpen size={16} />
                            Read Resource
                            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                          </>
                        ) : resource.downloadUrl ? (
                          <>
                            <Download size={16} />
                            Download Guide
                          </>
                        ) : resource.type === 'Coming Soon' ? (
                          <span className="text-stone-400 italic">Coming soon...</span>
                        ) : (
                          <span className="text-stone-400">Content in development</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-16">
              <p className="text-stone-500 font-body">No resources found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Resource Detail Modal */}
      <AnimatePresence>
        {selectedResource?.detailed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto resource-modal-overlay"
            onClick={handleCloseResource}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              id="article-print-area"
              className="bg-cream rounded-2xl max-w-3xl w-full my-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative">
                <img
                  src={selectedResource.detailed.image}
                  alt={selectedResource.detailed.title}
                  className="w-full h-56 object-cover object-bottom rounded-t-2xl"
                />
                <div className="absolute top-4 right-4 flex gap-2 no-print">
                  <button
                    onClick={() => window.print()}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    title="Print or save as PDF"
                    data-testid="print-resource-pdf"
                  >
                    <Printer size={20} className="text-forest" />
                  </button>
                  <button
                    onClick={() => copyResourceLink(selectedResource.id)}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    title="Copy shareable link"
                    data-testid="copy-resource-link"
                  >
                    {copied ? <Check size={20} className="text-emerald-600" /> : <Link2 size={20} className="text-forest" />}
                  </button>
                  <button
                    onClick={handleCloseResource}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    data-testid="close-resource-modal"
                  >
                    <X size={20} className="text-forest" />
                  </button>
                </div>
                {copied && (
                  <div className="absolute top-16 right-4 bg-emerald-600 text-white text-xs font-body px-3 py-1 rounded-full shadow-lg">
                    Link copied!
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-forest/80 to-transparent p-6">
                  <p className="text-cream/80 font-body text-sm mb-1">{selectedResource.detailed.subtitle}</p>
                  <h2 className="font-heading text-3xl text-white">
                    {selectedResource.detailed.title}
                  </h2>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-8 space-y-8">
                {/* Top Note - prominent callout for who this resource is for */}
                {selectedResource.detailed.topNote && (
                  <div className="bg-terracotta text-white rounded-xl p-5 text-center shadow-md -mt-4">
                    <p className="font-heading text-lg md:text-xl italic">
                      {selectedResource.detailed.topNote}
                    </p>
                  </div>
                )}

                {/* Introduction */}
                <div>
                  <p className="text-stone-600 font-body leading-relaxed">
                    {selectedResource.detailed.introduction}
                  </p>
                </div>

                {/* Designed for Flow (All Healing Is Self Healing) */}
                {selectedResource.detailed.designedForFlow && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.designedForFlow.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.designedForFlow.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.designedForFlow.intro}</p>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.designedForFlow.chineseMedicine}</p>
                    <div className="bg-terracotta/10 rounded-lg p-4 mb-3">
                      <p className="font-heading text-lg text-forest italic text-center">"{selectedResource.detailed.designedForFlow.insight}"</p>
                    </div>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.designedForFlow.signals}</p>
                  </div>
                )}

                {/* Stagnation Section */}
                {selectedResource.detailed.stagnation && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.stagnation.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.stagnation.title}</h3>
                    </div>
                    <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.stagnation.intro}</p>
                    <div className="bg-cream/10 rounded-lg p-4 mb-4">
                      <p className="font-body text-sm text-cream/80 mb-2">The process of stagnation:</p>
                      <ul className="space-y-1">
                        {selectedResource.detailed.stagnation.process.map((step, idx) => (
                          <li key={idx} className="text-cream/90 font-body text-sm flex items-center gap-2">
                            <span className="text-terracotta">→</span>{step}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mb-4">
                      {selectedResource.detailed.stagnation.systems.map((system, idx) => (
                        <div key={idx} className="bg-cream/10 rounded-lg p-3">
                          <p className="font-body font-medium text-sm text-terracotta">{system.name}</p>
                          <p className="font-body text-xs text-cream/80">{system.effect}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-cream/80 font-body text-sm">{selectedResource.detailed.stagnation.compensation}</p>
                  </div>
                )}

                {/* Organs Intelligence */}
                {selectedResource.detailed.organsIntelligence && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.organsIntelligence.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.organsIntelligence.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.organsIntelligence.intro}</p>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.organsIntelligence.communication}</p>
                    <div className="bg-terracotta/10 rounded-lg p-4 mb-3">
                      <p className="text-stone-700 font-body text-sm">{selectedResource.detailed.organsIntelligence.symptomLocation}</p>
                    </div>
                    <p className="text-forest font-body font-medium text-sm">{selectedResource.detailed.organsIntelligence.healingFocus}</p>
                  </div>
                )}

                {/* What Interrupts Flow */}
                {selectedResource.detailed.interruptsFlow && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.interruptsFlow.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.interruptsFlow.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.interruptsFlow.intro}</p>
                    
                    {/* Thoughts and Beliefs */}
                    <div className="bg-stone-100 rounded-xl p-5">
                      <h4 className="font-heading text-lg text-forest mb-3">{selectedResource.detailed.interruptsFlow.thoughtsBeliefs.title}</h4>
                      <ul className="space-y-2">
                        {selectedResource.detailed.interruptsFlow.thoughtsBeliefs.points.map((point, idx) => (
                          <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                            <span className="text-terracotta">•</span>{point}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Unprocessed Emotion */}
                    <div className="bg-terracotta/10 rounded-xl p-5">
                      <h4 className="font-heading text-lg text-forest mb-3">{selectedResource.detailed.interruptsFlow.unprocessedEmotion.title}</h4>
                      <ul className="space-y-2 mb-3">
                        {selectedResource.detailed.interruptsFlow.unprocessedEmotion.points.map((point, idx) => (
                          <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                            <span className="text-terracotta">•</span>{point}
                          </li>
                        ))}
                      </ul>
                      <p className="text-forest font-body italic text-sm">{selectedResource.detailed.interruptsFlow.unprocessedEmotion.process}</p>
                    </div>
                    
                    {/* Trauma */}
                    <div className="bg-forest text-cream rounded-xl p-5">
                      <h4 className="font-heading text-lg text-terracotta mb-3">{selectedResource.detailed.interruptsFlow.trauma.title}</h4>
                      <p className="text-cream/90 font-body text-sm mb-3">{selectedResource.detailed.interruptsFlow.trauma.intro}</p>
                      <p className="text-cream/90 font-body text-sm mb-3">{selectedResource.detailed.interruptsFlow.trauma.freeze}</p>
                      <p className="text-cream/80 font-body text-sm mb-3">{selectedResource.detailed.interruptsFlow.trauma.problem}</p>
                      <p className="text-terracotta font-body font-medium text-sm">{selectedResource.detailed.interruptsFlow.trauma.key}</p>
                    </div>
                  </div>
                )}

                {/* Why Healing Doesn't Work */}
                {selectedResource.detailed.whyHealingDoesntWork && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.whyHealingDoesntWork.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.whyHealingDoesntWork.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.whyHealingDoesntWork.intro}</p>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.whyHealingDoesntWork.survival}</p>
                    <div className="bg-terracotta/10 rounded-lg p-4 mb-3">
                      <p className="text-stone-700 font-body italic text-center">"{selectedResource.detailed.whyHealingDoesntWork.nervousSystem}"</p>
                    </div>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.whyHealingDoesntWork.integration}</p>
                  </div>
                )}

                {/* Role of Grounded Wellness */}
                {selectedResource.detailed.roleOfGroundedWellness && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.roleOfGroundedWellness.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.roleOfGroundedWellness.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.roleOfGroundedWellness.objective}</p>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.roleOfGroundedWellness.methods}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedResource.detailed.roleOfGroundedWellness.principles.map((principle, idx) => (
                        <span key={idx} className="px-3 py-1 bg-forest text-cream rounded-full text-sm font-body">{principle}</span>
                      ))}
                    </div>
                    <p className="text-stone-500 font-body text-sm italic">{selectedResource.detailed.roleOfGroundedWellness.approach}</p>
                  </div>
                )}

                {/* Healing as Permission */}
                {selectedResource.detailed.healingAsPermission && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.healingAsPermission.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.healingAsPermission.title}</h3>
                    </div>
                    <p className="text-cream/90 font-body mb-3">{selectedResource.detailed.healingAsPermission.intro}</p>
                    <p className="text-cream/80 font-body mb-3">{selectedResource.detailed.healingAsPermission.less}</p>
                    <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.healingAsPermission.permission}</p>
                    <p className="text-terracotta font-heading italic text-lg text-center">"{selectedResource.detailed.healingAsPermission.key}"</p>
                  </div>
                )}

                {/* Return to Intelligence */}
                {selectedResource.detailed.returnToIntelligence && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.returnToIntelligence.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.returnToIntelligence.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.returnToIntelligence.intro}</p>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.returnToIntelligence.foundation}</p>
                    <div className="bg-terracotta/10 rounded-lg p-4 mb-3">
                      <p className="text-forest font-heading italic text-center">"{selectedResource.detailed.returnToIntelligence.blueprint}"</p>
                    </div>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.returnToIntelligence.practitionerRole}</p>
                  </div>
                )}

                {/* Organ Emotions Table */}
                {selectedResource.detailed.organEmotions && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.organEmotions.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.organEmotions.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.organEmotions.intro}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-forest text-cream">
                            <th className="p-2 text-left font-body">Organ</th>
                            <th className="p-2 text-left font-body">Core Energy</th>
                            <th className="p-2 text-left font-body">Balanced</th>
                            <th className="p-2 text-left font-body">Stagnant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedResource.detailed.organEmotions.organs.map((organ, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-stone-50' : 'bg-white'}>
                              <td className="p-2 font-body font-medium text-forest">{organ.organ}</td>
                              <td className="p-2 font-body text-stone-600">{organ.energy}</td>
                              <td className="p-2 font-body text-emerald-600">{organ.balanced}</td>
                              <td className="p-2 font-body text-amber-600">{organ.stagnant}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-terracotta/10 rounded-lg p-4 mt-4">
                      <p className="text-forest font-heading italic text-center">"{selectedResource.detailed.organEmotions.key}"</p>
                    </div>
                  </div>
                )}

                {/* Constriction vs Expansion (All Healing Is Self Healing) */}
                {selectedResource.detailed.constrictionExpansion && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.constrictionExpansion.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.constrictionExpansion.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-stone-100 rounded-xl p-5">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.constrictionExpansion.constriction.title}</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.constrictionExpansion.constriction.description}</p>
                      </div>
                      <div className="bg-terracotta/10 rounded-xl p-5">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.constrictionExpansion.expansion.title}</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.constrictionExpansion.expansion.description}</p>
                      </div>
                    </div>
                    <p className="text-stone-600 font-body italic text-center">{selectedResource.detailed.constrictionExpansion.insight}</p>
                  </div>
                )}

                {/* What Creates Constriction */}
                {selectedResource.detailed.whatCreatesConstriction && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.whatCreatesConstriction.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.whatCreatesConstriction.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.whatCreatesConstriction.intro}</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.whatCreatesConstriction.signs.map((sign, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{sign}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-forest text-cream rounded-lg p-4 mb-4">
                      <p className="font-body font-medium mb-2">{selectedResource.detailed.whatCreatesConstriction.root}</p>
                      <p className="font-body text-sm text-cream/80">A subtle internal stance of:</p>
                      <ul className="mt-2 space-y-1">
                        {selectedResource.detailed.whatCreatesConstriction.stances.map((stance, idx) => (
                          <li key={idx} className="font-body text-sm italic">"{stance}"</li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.whatCreatesConstriction.impact}</p>
                  </div>
                )}

                {/* Expansion Section */}
                {selectedResource.detailed.expansionSection && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.expansionSection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.expansionSection.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.expansionSection.intro}</p>
                    <p className="font-body text-stone-500 mb-2">Expansion shows up when:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.expansionSection.showsUp.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">✓</span>{item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-forest font-heading italic">{selectedResource.detailed.expansionSection.key}</p>
                  </div>
                )}

                {/* Simplest Truth */}
                {selectedResource.detailed.simplestTruth && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.simplestTruth.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.simplestTruth.title}</h3>
                    </div>
                    <p className="font-body text-cream/90">{selectedResource.detailed.simplestTruth.content}</p>
                  </div>
                )}

                {/* Best Friend */}
                {selectedResource.detailed.bestFriend && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.bestFriend.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bestFriend.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.bestFriend.intro}</p>
                    <p className="font-body text-stone-500 mb-2">Non-abandonment means:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.bestFriend.means.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-forest font-heading italic">{selectedResource.detailed.bestFriend.key}</p>
                  </div>
                )}

                {/* Mindfulness */}
                {selectedResource.detailed.mindfulness && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.mindfulness.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.mindfulness.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.mindfulness.intro}</p>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.mindfulness.insight}</p>
                    <p className="font-body text-stone-500 mb-2">Mindfulness allows us to pause and ask:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.mindfulness.questions.map((q, idx) => (
                        <li key={idx} className="text-stone-600 font-body text-sm italic">"{q}"</li>
                      ))}
                    </ul>
                    <div className="bg-forest text-cream rounded-lg p-4">
                      <p className="font-body text-sm">{selectedResource.detailed.mindfulness.truth}</p>
                    </div>
                  </div>
                )}

                {/* The Shift */}
                {selectedResource.detailed.theShift && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.theShift.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.theShift.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-2">{selectedResource.detailed.theShift.intro}</p>
                    <p className="text-stone-500 font-body text-sm mb-4 italic">{selectedResource.detailed.theShift.note}</p>
                    <p className="font-body text-stone-500 mb-3">Expansion comes online when we move from:</p>
                    <div className="space-y-2 mb-4">
                      {selectedResource.detailed.theShift.moves.map((move, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-stone-600 font-body text-sm">
                          <span className="text-stone-400">{move.from}</span>
                          <span className="text-terracotta">→</span>
                          <span className="text-forest font-medium">{move.to}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-forest font-heading italic">{selectedResource.detailed.theShift.insight}</p>
                  </div>
                )}

                {/* Final Thought */}
                {selectedResource.detailed.finalThought && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.finalThought.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.finalThought.title}</h3>
                    </div>
                    <p className="font-body text-cream/90 leading-relaxed">{selectedResource.detailed.finalThought.content}</p>
                  </div>
                )}

                {/* Key Message (Try Not to Lose the Plot) */}
                {selectedResource.detailed.keyMessage && (
                  <div className="bg-terracotta/10 border-l-4 border-terracotta p-5 rounded-r-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.keyMessage.icon} size={20} />
                      <h3 className="font-heading text-lg text-forest">{selectedResource.detailed.keyMessage.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">
                      {selectedResource.detailed.keyMessage.content}
                    </p>
                  </div>
                )}


                {/* Survival Mode */}
                {selectedResource.detailed.survivalMode && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.survivalMode.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.survivalMode.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.survivalMode.content}</p>
                    {selectedResource.detailed.survivalMode.keyInsight && (
                      <div className="bg-forest text-cream p-4 rounded-lg">
                        <p className="font-body italic">{selectedResource.detailed.survivalMode.keyInsight}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Exercise Insight with Story */}
                {selectedResource.detailed.exerciseInsight && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.exerciseInsight.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.exerciseInsight.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.exerciseInsight.intro}</p>
                    
                    {selectedResource.detailed.exerciseInsight.story && (
                      <div className="bg-stone-100 rounded-xl p-5 mb-4">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.exerciseInsight.story.title}</h4>
                        <p className="text-stone-600 font-body text-sm italic">{selectedResource.detailed.exerciseInsight.story.content}</p>
                      </div>
                    )}
                    
                    {selectedResource.detailed.exerciseInsight.takeaway && (
                      <p className="text-amber-700 bg-amber-50 px-4 py-3 rounded-lg font-body text-sm">
                        💡 {selectedResource.detailed.exerciseInsight.takeaway}
                      </p>
                    )}
                  </div>
                )}

                {/* Core Guidance */}
                {selectedResource.detailed.coreGuidance && (
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-4 flex items-center gap-2">
                      <Sparkles className="text-terracotta" size={24} />
                      Core Guidance
                    </h3>
                    <div className="space-y-4">
                      {selectedResource.detailed.coreGuidance.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <SectionIcon icon={item.icon} size={20} />
                            <h4 className="font-heading text-lg text-forest">{item.title}</h4>
                          </div>
                          <p className="text-stone-600 font-body text-sm">{item.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add-Ins */}
                {selectedResource.detailed.addIns && (
                  <div className="bg-emerald-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.addIns.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.addIns.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.addIns.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3">
                          <span className="font-body font-semibold text-forest text-sm">{item.tip}</span>
                          <p className="text-stone-600 font-body text-xs mt-1">{item.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Things to Toss */}
                {selectedResource.detailed.thingsToToss && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.thingsToToss.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.thingsToToss.title}</h3>
                    </div>
                    <p className="text-stone-500 font-body text-sm mb-4">{selectedResource.detailed.thingsToToss.subtitle}</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.thingsToToss.items.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-red-400">✕</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    {selectedResource.detailed.thingsToToss.innerCriticNote && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-stone-600 font-body text-sm italic">
                          <strong>About the Inner Critic:</strong> {selectedResource.detailed.thingsToToss.innerCriticNote}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Ask For Help */}
                {selectedResource.detailed.askForHelp && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.askForHelp.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.askForHelp.title}</h3>
                    </div>
                    <p className="font-body text-cream/90">{selectedResource.detailed.askForHelp.content}</p>
                  </div>
                )}

                {/* Personal Story */}
                {selectedResource.detailed.personalStory && (
                  <div className="bg-terracotta/10 border-l-4 border-terracotta p-5 rounded-r-lg">
                    <h3 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.personalStory.title}</h3>
                    <p className="text-stone-600 font-body text-sm italic">
                      {selectedResource.detailed.personalStory.content}
                    </p>
                  </div>
                )}

                {/* Trauma Note (Sleep) */}
                {selectedResource.detailed.traumaNote && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.traumaNote.icon} size={20} />
                      <h3 className="font-heading text-lg text-amber-800">{selectedResource.detailed.traumaNote.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.traumaNote.content}</p>
                  </div>
                )}

                {/* Cortisol Science (Sleep) */}
                {selectedResource.detailed.cortisolScience && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.cortisolScience.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.cortisolScience.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.cortisolScience.intro}</p>
                    <div className="bg-forest text-cream p-4 rounded-lg mb-4">
                      <code className="font-body text-sm">{selectedResource.detailed.cortisolScience.codeFormula}</code>
                    </div>
                    <p className="text-stone-500 font-body text-sm italic mb-2">{selectedResource.detailed.cortisolScience.insight}</p>
                    <p className="text-emerald-700 bg-emerald-50 px-4 py-3 rounded-lg font-body text-sm mb-6">{selectedResource.detailed.cortisolScience.normalCurve}</p>
                    
                    {/* Hormone Curve Graphics */}
                    {selectedResource.detailed.cortisolScience.graphics && (
                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        {selectedResource.detailed.cortisolScience.graphics.normalCurve && (
                          <div className="bg-stone-50 rounded-xl p-4">
                            <img 
                              src={selectedResource.detailed.cortisolScience.graphics.normalCurve.image} 
                              alt={selectedResource.detailed.cortisolScience.graphics.normalCurve.title}
                              className="w-full rounded-lg mb-3"
                            />
                            <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.cortisolScience.graphics.normalCurve.title}</h4>
                            <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.cortisolScience.graphics.normalCurve.caption}</p>
                          </div>
                        )}
                        {selectedResource.detailed.cortisolScience.graphics.exaggeratedCurve && (
                          <div className="bg-rose-50 rounded-xl p-4">
                            <img 
                              src={selectedResource.detailed.cortisolScience.graphics.exaggeratedCurve.image} 
                              alt={selectedResource.detailed.cortisolScience.graphics.exaggeratedCurve.title}
                              className="w-full rounded-lg mb-3"
                            />
                            <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.cortisolScience.graphics.exaggeratedCurve.title}</h4>
                            <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.cortisolScience.graphics.exaggeratedCurve.caption}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Sleep Hygiene Tips */}
                {selectedResource.detailed.sleepHygieneTips && (
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-4">Sleep Hygiene Tips</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedResource.detailed.sleepHygieneTips.map((tip, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <SectionIcon icon={tip.icon} size={20} />
                            <h4 className="font-heading text-lg text-forest">{tip.title}</h4>
                          </div>
                          <p className="text-stone-600 font-body text-sm">{tip.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hormonal Pathways (Sleep) */}
                {selectedResource.detailed.hormonalPathways && (
                  <div className="bg-violet-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.hormonalPathways.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.hormonalPathways.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body text-sm mb-4">{selectedResource.detailed.hormonalPathways.intro}</p>
                    <div className="space-y-3">
                      {selectedResource.detailed.hormonalPathways.pathways.map((pathway, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span>{pathway.icon}</span>
                            <h4 className="font-body font-semibold text-forest">{pathway.name}</h4>
                          </div>
                          <p className="text-stone-600 font-body text-sm mb-2">{pathway.description}</p>
                          {pathway.supplements && (
                            <ul className="space-y-1">
                              {pathway.supplements.map((sup, i) => (
                                <li key={i} className="text-stone-500 font-body text-xs flex gap-2">
                                  <span className="text-violet-500">•</span>{sup}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Supplements */}
                {selectedResource.detailed.otherSupplements && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.otherSupplements.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.otherSupplements.title}</h3>
                    </div>
                    <p className="text-amber-700 bg-amber-50 px-3 py-2 rounded-lg text-xs font-body mb-3">⚠️ {selectedResource.detailed.otherSupplements.note}</p>
                    <ul className="space-y-2">
                      {selectedResource.detailed.otherSupplements.items.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Stress Section (Sleep) */}
                {selectedResource.detailed.stressSection && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.stressSection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.stressSection.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.stressSection.intro}</p>
                    <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                      <p className="text-emerald-700 font-body text-sm">💚 {selectedResource.detailed.stressSection.goodNews}</p>
                    </div>
                    {selectedResource.detailed.stressSection.framework && (
                      <div className="space-y-3">
                        <h4 className="font-heading text-lg text-forest">{selectedResource.detailed.stressSection.framework.title}</h4>
                        {selectedResource.detailed.stressSection.framework.steps.map((step, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4 shadow-sm">
                            <span className="font-body font-semibold text-terracotta">{step.step}:</span>
                            <span className="text-stone-600 font-body text-sm"> {step.content}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Self Care Tips */}
                {selectedResource.detailed.selfCareTips && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <h3 className="font-heading text-xl text-forest mb-4">Self-Care Essentials</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.selfCareTips.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3">
                          <span className="font-body font-semibold text-forest text-sm">{item.tip}</span>
                          <p className="text-stone-600 font-body text-xs mt-1">{item.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bedtime Adjustment (Sleep) */}
                {selectedResource.detailed.bedtimeAdjustment && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.bedtimeAdjustment.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.bedtimeAdjustment.title}</h3>
                    </div>
                    <p className="text-cream/90 font-body mb-3">{selectedResource.detailed.bedtimeAdjustment.intro}</p>
                    <p className="text-cream/80 font-body text-sm mb-2">{selectedResource.detailed.bedtimeAdjustment.method}</p>
                    <div className="bg-cream/20 rounded-lg p-3">
                      <p className="font-body text-sm">{selectedResource.detailed.bedtimeAdjustment.example}</p>
                    </div>
                  </div>
                )}

                {/* Core Framework (Stress) */}
                {selectedResource.detailed.coreFramework && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.coreFramework.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.coreFramework.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.coreFramework.intro}</p>
                    <div className="space-y-4">
                      {selectedResource.detailed.coreFramework.steps.map((step, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-body font-bold">
                              {step.number}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span>{step.icon}</span>
                                <h4 className="font-heading text-lg text-forest">{step.name}</h4>
                              </div>
                              <p className="text-stone-500 font-body text-sm italic mb-2">{step.question}</p>
                              <p className="text-stone-600 font-body text-sm">{step.content}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Self Care Section (Stress) */}
                {selectedResource.detailed.selfCareSection && (
                  <div className="bg-emerald-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.selfCareSection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.selfCareSection.title}</h3>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.selfCareSection.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3">
                          <span className="font-body font-semibold text-forest text-sm">{item.tip}</span>
                          <p className="text-stone-600 font-body text-xs mt-1">{item.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Important Truth (Stress) */}
                {selectedResource.detailed.importantTruth && (
                  <div className="bg-terracotta text-white rounded-xl p-6 text-center">
                    <span className="text-3xl mb-3 block">{selectedResource.detailed.importantTruth.icon}</span>
                    <p className="font-heading text-xl">{selectedResource.detailed.importantTruth.content}</p>
                  </div>
                )}

                {/* Reflection Questions */}
                {selectedResource.detailed.reflectionQuestions && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-heading text-xl text-forest mb-4">Questions to Consider</h3>
                    <ul className="space-y-3">
                      {selectedResource.detailed.reflectionQuestions.map((q, idx) => (
                        <li key={idx} className="flex gap-3 text-stone-600 font-body">
                          <span className="w-6 h-6 bg-terracotta/20 text-terracotta rounded-full flex items-center justify-center text-sm flex-shrink-0">{idx + 1}</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Emotions Tool (Stress) */}
                {selectedResource.detailed.emotionsTool && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.emotionsTool.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.emotionsTool.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.emotionsTool.intro}</p>
                    <div className="space-y-2 mb-4">
                      {selectedResource.detailed.emotionsTool.template.map((line, idx) => (
                        <div key={idx} className="bg-stone-50 rounded-lg p-3 font-body text-forest">{line}</div>
                      ))}
                    </div>
                    <div className="bg-terracotta/10 rounded-lg p-4">
                      <p className="text-stone-500 font-body text-xs mb-2">Example:</p>
                      {selectedResource.detailed.emotionsTool.example.map((ex, idx) => (
                        <p key={idx} className="text-stone-600 font-body text-sm italic">{ex}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Parenting Insight (Stress) */}
                {selectedResource.detailed.parentingInsight && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.parentingInsight.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.parentingInsight.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.parentingInsight.intro}</p>
                    <div className="grid md:grid-cols-3 gap-3 mb-4">
                      {selectedResource.detailed.parentingInsight.parentingTypes.map((type, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3">
                          <h4 className="font-body font-semibold text-forest text-sm mb-1">{type.type}</h4>
                          <p className="text-stone-500 font-body text-xs">{type.description}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-stone-600 font-body text-sm italic">{selectedResource.detailed.parentingInsight.reflection}</p>
                  </div>
                )}

                {/* 8 Reasons (Why We Suck at Recognizing Stress) */}
                {selectedResource.detailed.eightReasons && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.eightReasons.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.eightReasons.title}</h3>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.eightReasons.reasons.map((reason, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-start gap-4">
                            <div className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0 font-body font-bold text-sm">
                              {reason.number}
                            </div>
                            <div>
                              <h4 className="font-heading text-lg text-forest mb-1">{reason.title}</h4>
                              <p className="text-stone-600 font-body text-sm">{reason.content}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottom Line */}
                {selectedResource.detailed.bottomLine && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.bottomLine.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.bottomLine.title}</h3>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.bottomLine.points.map((point, idx) => (
                        <li key={idx} className="flex gap-2 text-cream/90 font-body text-sm">
                          <span>•</span>{point}
                        </li>
                      ))}
                    </ul>
                    <p className="text-cream font-body italic border-t border-cream/30 pt-3">{selectedResource.detailed.bottomLine.insight}</p>
                  </div>
                )}

                {/* Cortisol Impact */}
                {selectedResource.detailed.cortisolImpact && (
                  <div className="bg-amber-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.cortisolImpact.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.cortisolImpact.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.cortisolImpact.content}</p>
                    {selectedResource.detailed.cortisolImpact.normalCurveImage && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                          <img src={selectedResource.detailed.cortisolImpact.normalCurveImage} alt="Normal cortisol curve" className="w-full object-cover" />
                          <p className="text-sm text-stone-500 font-body p-3 leading-relaxed">{selectedResource.detailed.cortisolImpact.normalCurveCaption}</p>
                        </div>
                        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                          <img src={selectedResource.detailed.cortisolImpact.exaggeratedImage} alt="Exaggerated stress response" className="w-full object-cover" />
                          <p className="text-sm text-stone-500 font-body p-3 leading-relaxed">{selectedResource.detailed.cortisolImpact.exaggeratedCaption}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Rebuilding Awareness */}
                {selectedResource.detailed.rebuildingAwareness && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.rebuildingAwareness.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.rebuildingAwareness.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.rebuildingAwareness.intro}</p>
                    <div className="space-y-3">
                      {selectedResource.detailed.rebuildingAwareness.remedies.map((remedy, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-start gap-4">
                            <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 font-body font-bold text-sm">
                              {remedy.number}
                            </div>
                            <div>
                              <h4 className="font-heading text-lg text-forest mb-1">{remedy.title}</h4>
                              <p className="text-stone-600 font-body text-sm">{remedy.content}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nervous System Info */}
                {selectedResource.detailed.nervousSystemInfo && (
                  <div className="bg-violet-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.nervousSystemInfo.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.nervousSystemInfo.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.nervousSystemInfo.content}</p>
                    <p className="text-emerald-700 bg-emerald-50 px-4 py-3 rounded-lg font-body text-sm mb-4">💚 {selectedResource.detailed.nervousSystemInfo.goodNews}</p>
                    {selectedResource.detailed.nervousSystemInfo.stressFunnelImage && (
                      <div className="bg-white rounded-lg overflow-hidden shadow-sm max-w-md mx-auto mb-4">
                        <img src={selectedResource.detailed.nervousSystemInfo.stressFunnelImage} alt="The Stress Funnel" className="w-full object-cover" />
                        <p className="text-xs text-stone-400 font-body p-2 text-center italic">{selectedResource.detailed.nervousSystemInfo.stressFunnelCaption}</p>
                      </div>
                    )}
                    {selectedResource.detailed.nervousSystemInfo.stressFunnelContent && (
                      <p className="text-stone-600 font-body text-sm leading-relaxed">{selectedResource.detailed.nervousSystemInfo.stressFunnelContent}</p>
                    )}
                  </div>
                )}

                {/* Reframe */}
                {selectedResource.detailed.reframe && (
                  <div className="bg-terracotta text-white rounded-xl p-6 text-center">
                    <h3 className="font-heading text-xl mb-2">{selectedResource.detailed.reframe.title}</h3>
                    <p className="font-body text-lg">{selectedResource.detailed.reframe.content}</p>
                  </div>
                )}

                {/* Key Insights */}
                {selectedResource.detailed.keyInsights && (
                  <div>
                    <h3 className="font-heading text-xl text-forest mb-4">Wisdom to Consider</h3>
                    <div className="space-y-4">
                      {selectedResource.detailed.keyInsights.map((insight, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <p className="text-stone-500 font-body text-xs uppercase tracking-wider mb-2">{insight.source}</p>
                          <p className="font-heading text-lg text-forest italic mb-2">"{insight.quote}"</p>
                          <p className="text-stone-600 font-body text-sm">{insight.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 6 Foundations */}
                {selectedResource.detailed.sixFoundations && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={selectedResource.detailed.sixFoundations.icon} size={24} />
                      <div>
                        <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.sixFoundations.title}</h3>
                        <p className="text-stone-500 font-body text-sm">{selectedResource.detailed.sixFoundations.subtitle}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {selectedResource.detailed.sixFoundations.items.map((item, idx) => (
                        <div key={idx} className="text-center p-3 bg-stone-50 rounded-lg">
                          <span className="text-2xl block mb-1">{item.icon}</span>
                          <span className="text-stone-600 font-body text-xs">{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeline Guidance */}
                {selectedResource.detailed.timelineGuidance && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <SectionIcon icon={selectedResource.detailed.timelineGuidance.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.timelineGuidance.title}</h3>
                    </div>
                    <p className="font-body text-cream/90 leading-relaxed">
                      {selectedResource.detailed.timelineGuidance.content}
                    </p>
                  </div>
                )}

                {/* Stages of Healing */}
                {selectedResource.detailed.stagesOfHealing && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={selectedResource.detailed.stagesOfHealing.icon} size={24} />
                      <div>
                        <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.stagesOfHealing.title}</h3>
                        <p className="text-stone-500 font-body text-sm">{selectedResource.detailed.stagesOfHealing.subtitle}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.stagesOfHealing.stages.map((stage, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex gap-4">
                          <div className="w-8 h-8 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-body font-semibold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <h4 className="font-heading text-lg text-forest">{stage.name}</h4>
                            <p className="text-stone-600 font-body text-sm">{stage.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Practical Guidance */}
                {selectedResource.detailed.practicalGuidance && (
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-4 flex items-center gap-2">
                      <Sparkles className="text-terracotta" size={24} />
                      Practical Guidance
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedResource.detailed.practicalGuidance.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <SectionIcon icon={item.icon} size={20} />
                            <h4 className="font-heading text-lg text-forest">{item.title}</h4>
                          </div>
                          <p className="text-stone-600 font-body text-sm">{item.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mindfulness Section */}
                {selectedResource.detailed.mindfulnessSection && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={selectedResource.detailed.mindfulnessSection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.mindfulnessSection.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.mindfulnessSection.intro}</p>
                    
                    {selectedResource.detailed.mindfulnessSection.attitudinalFactors && (
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-body font-semibold text-forest mb-2">
                          {selectedResource.detailed.mindfulnessSection.attitudinalFactors.title}
                        </h4>
                        <p className="text-stone-500 font-body text-xs mb-3 italic">
                          {selectedResource.detailed.mindfulnessSection.attitudinalFactors.note}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedResource.detailed.mindfulnessSection.attitudinalFactors.factors.map((factor, idx) => (
                            <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-body">
                              {idx + 1}. {factor}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Emotions Section */}
                {selectedResource.detailed.emotionsSection && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={selectedResource.detailed.emotionsSection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.emotionsSection.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.emotionsSection.intro}</p>
                    
                    {selectedResource.detailed.emotionsSection.tool && (
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.emotionsSection.tool.title}</h4>
                        <p className="text-stone-600 font-body text-sm mb-4">{selectedResource.detailed.emotionsSection.tool.description}</p>
                        
                        <div className="space-y-2 mb-4">
                          {selectedResource.detailed.emotionsSection.tool.steps.map((step, idx) => (
                            <div key={idx} className="bg-stone-50 rounded-lg p-3 font-body text-forest">
                              {step}
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-terracotta/10 rounded-lg p-4 mb-3">
                          <p className="text-stone-500 font-body text-xs mb-2">Example:</p>
                          {selectedResource.detailed.emotionsSection.tool.example.map((ex, idx) => (
                            <p key={idx} className="text-stone-600 font-body text-sm italic">{ex}</p>
                          ))}
                        </div>
                        
                        <p className="text-amber-700 bg-amber-50 px-3 py-2 rounded-lg text-sm font-body">
                          💡 {selectedResource.detailed.emotionsSection.tool.tip}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Metaphysics */}
                {selectedResource.detailed.metaphysics && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={selectedResource.detailed.metaphysics.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.metaphysics.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.metaphysics.intro}</p>
                    
                    <div className="grid md:grid-cols-2 gap-3 mb-4">
                      {selectedResource.detailed.metaphysics.examples.map((ex, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="font-body font-semibold text-forest">{ex.area}:</span>
                          <span className="text-stone-600 font-body text-sm"> {ex.meaning}</span>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-stone-500 font-body text-sm italic bg-stone-100 p-3 rounded-lg">
                      {selectedResource.detailed.metaphysics.note}
                    </p>
                  </div>
                )}

                {/* Energy Section */}
                {selectedResource.detailed.energySection && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={selectedResource.detailed.energySection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.energySection.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {selectedResource.detailed.energySection.items.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Quick Reference */}
                {selectedResource.detailed.quickReference && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <SectionIcon icon={selectedResource.detailed.quickReference.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.quickReference.title}</h3>
                    </div>
                    <p className="text-stone-500 font-body text-sm mb-4">{selectedResource.detailed.quickReference.subtitle}</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.quickReference.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3">
                          <span className="font-body font-semibold text-forest">{item.tip}</span>
                          <p className="text-stone-600 font-body text-xs mt-1">{item.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Main Steps (for Adrenal Support) */}
                {selectedResource.detailed.mainSteps && (
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-4">8 Steps to Adrenal Recovery</h3>
                    <div className="space-y-4">
                      {selectedResource.detailed.mainSteps.map((step, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-body font-bold">
                              {step.number}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <SectionIcon icon={step.icon} size={20} />
                                <h4 className="font-heading text-lg text-forest">{step.title}</h4>
                              </div>
                              <p className="text-stone-600 font-body text-sm mb-3">{step.content}</p>
                              {step.tips && (
                                <ul className="space-y-1">
                                  {step.tips.map((tip, i) => (
                                    <li key={i} className="flex gap-2 text-stone-500 font-body text-xs">
                                      <span className="text-terracotta">•</span>{tip}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {step.supplements && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {step.supplements.map((sup, i) => (
                                    <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-body">{sup}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cortisol Guide */}
                {selectedResource.detailed.cortisolGuide && (
                  <div className="bg-amber-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.cortisolGuide.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.cortisolGuide.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {selectedResource.detailed.cortisolGuide.items.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-amber-600">✓</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Vitamins and Minerals */}
                {selectedResource.detailed.vitaminsAndMinerals && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.vitaminsAndMinerals.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.vitaminsAndMinerals.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.vitaminsAndMinerals.items.map((item, idx) => (
                        <div key={idx} className="bg-stone-50 rounded-lg p-3">
                          <span className="font-body font-semibold text-forest text-sm">{item.name}</span>
                          <p className="text-stone-500 font-body text-xs">{item.benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Adaptogens */}
                {selectedResource.detailed.adaptogens && (
                  <div className="bg-emerald-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.adaptogens.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.adaptogens.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.adaptogens.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3">
                          <span className="font-body font-semibold text-forest text-sm">{item.name}</span>
                          <p className="text-stone-500 font-body text-xs">{item.benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Food Guidelines (for Thumb Rules) */}
                {selectedResource.detailed.foodGuidelines && (
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-4 flex items-center gap-2">
                      <Sparkles className="text-terracotta" size={24} />
                      Food Guidelines
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedResource.detailed.foodGuidelines.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <SectionIcon icon={item.icon} size={20} />
                            <h4 className="font-heading text-lg text-forest">{item.title}</h4>
                          </div>
                          <p className="text-stone-600 font-body text-sm">{item.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Love Philosophy */}
                {selectedResource.detailed.lovePhilosophy && (
                  <div className="bg-terracotta/10 border-l-4 border-terracotta p-5 rounded-r-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.lovePhilosophy.icon} size={20} />
                      <h3 className="font-heading text-lg text-forest">{selectedResource.detailed.lovePhilosophy.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.lovePhilosophy.content}</p>
                  </div>
                )}

                {/* Meat Guide */}
                {selectedResource.detailed.meatGuide && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.meatGuide.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.meatGuide.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.meatGuide.intro}</p>
                    
                    <div className="space-y-4">
                      {/* Helpful */}
                      <div className="bg-emerald-50 rounded-xl p-4">
                        <h4 className="font-body font-semibold text-emerald-700 mb-2">{selectedResource.detailed.meatGuide.helpful.title}</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedResource.detailed.meatGuide.helpful.items.map((item, idx) => (
                            <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-body">✓ {item}</span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Misleading */}
                      <div className="bg-amber-50 rounded-xl p-4">
                        <h4 className="font-body font-semibold text-amber-700 mb-1">{selectedResource.detailed.meatGuide.misleading.title}</h4>
                        <p className="text-amber-600 font-body text-xs mb-2">{selectedResource.detailed.meatGuide.misleading.note}</p>
                        <div className="space-y-2">
                          {selectedResource.detailed.meatGuide.misleading.items.map((item, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-2">
                              <span className="font-body font-semibold text-amber-700 text-sm">⚠️ {item.term}:</span>
                              <span className="text-stone-600 font-body text-xs"> {item.explanation}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Unhelpful */}
                      <div className="bg-red-50 rounded-xl p-4">
                        <h4 className="font-body font-semibold text-red-700 mb-2">{selectedResource.detailed.meatGuide.unhelpful.title}</h4>
                        <div className="space-y-2">
                          {selectedResource.detailed.meatGuide.unhelpful.items.map((item, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-2">
                              <span className="font-body font-semibold text-red-700 text-sm">✕ {item.term}:</span>
                              <span className="text-stone-600 font-body text-xs"> {item.explanation}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Metabolic Typing */}
                {selectedResource.detailed.metabolicTyping && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.metabolicTyping.icon} size={20} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.metabolicTyping.title}</h3>
                    </div>
                    <p className="font-body text-cream/90">{selectedResource.detailed.metabolicTyping.content}</p>
                  </div>
                )}

                {/* Recommendations (Movies & Books for Thumb Rules) */}
                {selectedResource.detailed.recommendations && (
                  <div>
                    {selectedResource.detailed.recommendations.movies && (
                      <div className="mb-6">
                        <h3 className="font-heading text-xl text-forest mb-4">🎬 Recommended Watching</h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {selectedResource.detailed.recommendations.movies.map((movie, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-4 shadow-sm">
                              <p className="font-body font-semibold text-forest text-sm">{movie.title}</p>
                              <p className="text-stone-500 font-body text-xs">{movie.note}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedResource.detailed.recommendations.books && (
                      <div>
                        <h3 className="font-heading text-xl text-forest mb-4 flex items-center gap-2">
                          <BookOpen className="text-terracotta" size={20} />
                          Recommended Reading
                        </h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {selectedResource.detailed.recommendations.books.map((book, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex items-center gap-3">
                              <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center text-lg">📚</div>
                              <div>
                                <p className="font-body font-semibold text-forest text-sm">{book.title}</p>
                                {book.author && <p className="text-stone-500 font-body text-xs">{book.author}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Quotes */}
                {selectedResource.detailed.quotes && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    {selectedResource.detailed.quotes.map((quote, idx) => (
                      <p key={idx} className={`font-heading text-lg italic ${idx > 0 ? 'mt-4 pt-4 border-t border-cream/20' : ''}`}>
                        "{quote}"
                      </p>
                    ))}
                  </div>
                )}

                {/* Matrix Examples (9 Pillars) */}
                {selectedResource.detailed.matrixExamples && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <p className="text-stone-500 font-body text-sm mb-3">Everything is connected:</p>
                    <div className="space-y-2">
                      {selectedResource.detailed.matrixExamples.map((example, idx) => (
                        <p key={idx} className="text-forest font-body italic">{example}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Wheel Graphic (9 Pillars) */}
                {selectedResource.detailed.wheelGraphic && (
                  <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                    <img 
                      src={selectedResource.detailed.wheelGraphic} 
                      alt="9 Pillars Wellness Wheel" 
                      className="max-w-md mx-auto rounded-xl"
                    />
                  </div>
                )}

                {/* Pillars Intro (9 Pillars) */}
                {selectedResource.detailed.pillarsIntro && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.pillarsIntro.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.pillarsIntro.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4 whitespace-pre-line">{selectedResource.detailed.pillarsIntro.content}</p>
                    {selectedResource.detailed.pillarsIntro.question && (
                      <div className="bg-terracotta/10 rounded-lg p-4 mb-4">
                        <p className="text-forest font-body italic">{selectedResource.detailed.pillarsIntro.question}</p>
                      </div>
                    )}
                    {selectedResource.detailed.pillarsIntro.insight && (
                      <p className="text-terracotta font-heading text-lg">"{selectedResource.detailed.pillarsIntro.insight}"</p>
                    )}
                  </div>
                )}

                {/* The 9 Pillars Sections */}
                {selectedResource.detailed.sections && selectedResource.id === '9-pillars-wellness' && (
                  <div className="space-y-4">
                    <h3 className="font-heading text-2xl text-forest text-center mb-6">The 9 Pillars</h3>
                    {selectedResource.detailed.sections.map((pillar, idx) => (
                      <div key={idx} className={`rounded-xl p-6 ${idx % 2 === 0 ? 'bg-white shadow-sm' : 'bg-stone-50'}`}>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-heading text-lg">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-heading text-xl text-forest mb-2">{pillar.title.replace(/^\d+\.\s*/, '')}</h4>
                            <p className="text-stone-600 font-body mb-3">{pillar.content}</p>
                            
                            {pillar.insight && (
                              <div className="bg-terracotta/10 rounded-lg p-3 mb-3">
                                <p className="text-forest font-body text-sm italic">{pillar.insight}</p>
                              </div>
                            )}
                            
                            {pillar.items && (
                              <ul className="space-y-2 mb-3">
                                {pillar.items.map((item, i) => (
                                  <li key={i} className="flex gap-2 text-stone-600 font-body text-sm">
                                    <span className="text-terracotta">•</span>
                                    <span><strong className="text-forest">{item.term}:</strong> {item.definition}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {pillar.support && (
                              <div className="bg-emerald-50 rounded-lg p-3 mb-2">
                                <p className="text-stone-500 font-body text-xs uppercase tracking-wider mb-1">Support may include:</p>
                                <p className="text-stone-600 font-body text-sm">{pillar.support}</p>
                              </div>
                            )}
                            
                            {pillar.keywords && (
                              <p className="text-terracotta font-body text-sm italic mt-2">{pillar.keywords}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Three Influences (9 Pillars) */}
                {selectedResource.detailed.threeInfluences && (
                  <div className="bg-amber-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.threeInfluences.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.threeInfluences.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.threeInfluences.intro}</p>
                    <div className="space-y-3 mb-4">
                      {selectedResource.detailed.threeInfluences.influences.map((influence, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4">
                          <h4 className="font-heading text-lg text-forest mb-1">{influence.name}</h4>
                          <p className="text-stone-600 font-body text-sm">{influence.description}</p>
                        </div>
                      ))}
                    </div>
                    {selectedResource.detailed.threeInfluences.intentionNote && (
                      <div className="bg-forest text-cream p-4 rounded-lg">
                        <p className="font-body italic">{selectedResource.detailed.threeInfluences.intentionNote}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* One Year Journey (9 Pillars) */}
                {selectedResource.detailed.oneYearJourney && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.oneYearJourney.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.oneYearJourney.title}</h3>
                    </div>
                    {selectedResource.detailed.oneYearJourney.question && (
                      <p className="text-cream font-body mb-4 text-lg italic">{selectedResource.detailed.oneYearJourney.question}</p>
                    )}
                    <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.oneYearJourney.content}</p>
                    {selectedResource.detailed.oneYearJourney.transformation && (
                      <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.oneYearJourney.transformation}</p>
                    )}
                    {selectedResource.detailed.oneYearJourney.insight && (
                      <div className="bg-cream/10 rounded-lg p-4">
                        <p className="text-terracotta font-heading italic text-center">"{selectedResource.detailed.oneYearJourney.insight}"</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Books */}
                {selectedResource.detailed.books && (
                  <div>
                    <h3 className="font-heading text-xl text-forest mb-4 flex items-center gap-2">
                      <BookOpen className="text-terracotta" size={20} />
                      Recommended Reading
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.books.map((book, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex items-center gap-3">
                          <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center text-lg">
                            📚
                          </div>
                          <div>
                            <p className="font-body font-semibold text-forest text-sm">{book.title}</p>
                            {book.author && <p className="text-stone-500 font-body text-xs">{book.author}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Closing Thought */}
                {selectedResource.detailed.closingThought && (
                  <div className="bg-stone-100 rounded-xl p-6 text-center">
                    <Heart className="text-terracotta mx-auto mb-3" size={24} />
                    <p className="text-forest font-body">
                      {selectedResource.detailed.closingThought}
                    </p>
                  </div>
                )}

                {/* === ARTICLE RENDERING (section-based) === */}
                {(selectedResource.id === 'compassion-fatigue' || selectedResource.id === 'our-relationship-with-anxiety' || selectedResource.id === 'why-anxiety-creates-symptoms' || selectedResource.id === 'systemic-fungal-infections' || selectedResource.id === 'escaping-the-dopamine-cycle' || selectedResource.id === 'i-gotta-get-my-shift-together') && selectedResource.detailed && (
                  <div className="space-y-8">
                    {/* Opening */}
                    {selectedResource.detailed.opening && (
                      <div className="text-center py-4">
                        {selectedResource.detailed.opening.split('\n\n').map((para, i) => (
                          <p key={i} className={`font-body leading-relaxed mb-4 ${
                            para.includes('"Where did I go?"') 
                              ? 'font-heading text-3xl sm:text-4xl text-terracotta italic' 
                              : 'text-stone-600 text-lg'
                          }`}>{para.replace(/"/g, '')}{para.includes('"Where did I go?"') ? '' : ''}</p>
                        ))}
                      </div>
                    )}

                    {/* Sections */}
                    {selectedResource.detailed.sections?.map((section, sIdx) => (
                      <div key={section.id} className="relative">
                        {/* Section divider dot */}
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-2.5 h-2.5 rounded-full bg-terracotta flex-shrink-0" />
                          <h3 className="font-heading text-2xl text-forest">{section.title}</h3>
                        </div>

                        {/* Type: prose (generic) */}
                        {section.type === 'prose' && (
                          <div className="space-y-5">
                            {section.paragraphs?.map((p, i) => (
                              <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
                            ))}
                            {section.groups && (
                              <div className="space-y-4">
                                {section.groups.map((g, gi) => (
                                  <div
                                    key={gi}
                                    className={`rounded-xl p-6 border ${
                                      g.tone === 'tcm'
                                        ? 'bg-violet-50/60 border-violet-100'
                                        : g.tone === 'caution'
                                        ? 'bg-rose-50/50 border-rose-100'
                                        : g.tone === 'neutral'
                                        ? 'bg-stone-50 border-stone-100'
                                        : 'bg-emerald-50/50 border-emerald-100'
                                    }`}
                                  >
                                    <p className="font-body text-forest font-semibold mb-3">{g.title}</p>
                                    <ul className="space-y-2.5">
                                      {g.items.map((item, i) => (
                                        <li key={i} className="flex gap-3 items-start">
                                          <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                                          <span className="text-stone-600 font-body">{item}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            )}
                            {section.listItems && (
                              <div className="bg-white rounded-xl p-6 border border-stone-100">
                                {section.listTitle && (
                                  <p className="font-body text-forest font-medium mb-3">{section.listTitle}</p>
                                )}
                                <ul className="space-y-2.5">
                                  {section.listItems.map((item, i) => (
                                    <li key={i} className="flex gap-3 items-start">
                                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                                      <span className="text-stone-600 font-body">{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {section.callout && (
                              <div className="bg-terracotta/5 rounded-xl p-6 border-l-4 border-terracotta">
                                {section.callout.split('\n').map((line, i) => (
                                  <p key={i} className="text-forest font-body italic leading-relaxed">{line}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Type: intro-with-lists */}
                        {section.type === 'intro-with-lists' && (
                          <div className="space-y-5">
                            {section.intro.split('\n\n').map((p, i) => (
                              <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
                            ))}
                            
                            <div className="bg-white rounded-xl p-6 border border-stone-100">
                              <p className="font-body text-forest font-medium mb-3">{section.listTitle}</p>
                              <ul className="space-y-2.5">
                                {section.listItems.map((item, i) => (
                                  <li key={i} className="flex gap-3 items-start">
                                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                                    <span className="text-stone-600 font-body">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="bg-stone-50 rounded-xl p-6">
                              <p className="font-body text-forest font-medium mb-3">{section.maskTitle}</p>
                              <ul className="space-y-2.5">
                                {section.maskItems.map((item, i) => (
                                  <li key={i} className="flex gap-3 items-start">
                                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 flex-shrink-0" />
                                    <span className="text-stone-600 font-body">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {section.closing && (
                              <div className="bg-terracotta/5 rounded-xl p-6 border-l-4 border-terracotta">
                                {section.closing.split('\n').map((line, i) => (
                                  <p key={i} className="text-forest font-body italic leading-relaxed">{line}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Type: nervous-system */}
                        {section.type === 'nervous-system' && (
                          <div className="space-y-5">
                            {section.intro.split('\n').map((line, i) => (
                              <p key={i} className={`font-body leading-relaxed ${
                                line.includes('depletion') ? 'text-forest font-medium text-lg' : 'text-stone-600'
                              }`}>{line}</p>
                            ))}
                            
                            <div className="bg-forest text-cream rounded-xl p-6">
                              <p className="font-body text-cream/80 mb-3">{section.triggerTitle}</p>
                              <ul className="space-y-2 mb-5">
                                {section.triggers.map((t, i) => (
                                  <li key={i} className="flex gap-3 items-center">
                                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" />
                                    <span className="font-body text-cream">{t}</span>
                                  </li>
                                ))}
                              </ul>
                              {section.response.split('\n\n').map((p, i) => (
                                <p key={i} className="font-body text-cream/90 leading-relaxed mb-3">{p}</p>
                              ))}
                            </div>

                            <div className="bg-terracotta/10 rounded-xl p-6 text-center">
                              {section.truth.split('\n\n').map((p, i) => (
                                <p key={i} className={`font-body leading-relaxed mb-2 ${
                                  p.includes('We don\'t have') ? 'font-heading text-xl text-terracotta italic' : 'text-forest'
                                }`}>{p.replace(/"/g, '"')}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Type: turning-point */}
                        {section.type === 'turning-point' && (
                          <div className="space-y-5">
                            <p className="text-stone-600 font-body leading-relaxed">{section.content}</p>
                            
                            <div className="bg-stone-800 text-cream rounded-xl p-6 space-y-3">
                              {section.innerCritic.map((line, i) => (
                                <p key={i} className="font-accent text-lg text-cream/90 italic text-center">{line}</p>
                              ))}
                            </div>

                            <p className="text-stone-600 font-body leading-relaxed">{section.pushback}</p>

                            <div className="bg-terracotta/10 rounded-xl p-6 text-center border-2 border-terracotta/20">
                              {section.reframe.split('\n').map((line, i) => (
                                <p key={i} className="font-heading text-xl text-forest leading-relaxed">{line}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Type: shift */}
                        {section.type === 'shift' && (
                          <div className="space-y-5">
                            {section.intro.split('\n\n').map((p, i) => (
                              <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
                            ))}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {section.practices.map((practice, i) => (
                                <div key={i} className="bg-white rounded-xl p-5 border border-stone-100 flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Sparkles size={14} className="text-emerald-600" />
                                  </div>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">{practice}</p>
                                </div>
                              ))}
                            </div>

                            <div className="text-center py-4">
                              {section.signal.split('\n\n').map((p, i) => (
                                <p key={i} className={`font-body leading-relaxed mb-2 ${
                                  p.includes('allowed') ? 'font-heading text-xl text-forest italic' : 'text-stone-500'
                                }`}>{p.replace(/"/g, '"')}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Type: truth */}
                        {section.type === 'truth' && (
                          <div className="bg-gradient-to-br from-stone-50 to-terracotta/5 rounded-2xl p-8 space-y-3 text-center">
                            {section.lines.map((line, i) => (
                              <p key={i} className={`font-body leading-relaxed ${
                                i === section.lines.length - 1 
                                  ? 'font-heading text-xl text-terracotta italic mt-4 pt-4 border-t border-terracotta/20' 
                                  : i === section.lines.length - 2
                                  ? 'text-forest font-medium'
                                  : 'text-stone-600'
                              }`}>{line}</p>
                            ))}
                          </div>
                        )}

                        {/* Type: passionate */}
                        {section.type === 'passionate' && (
                          <div className="space-y-5">
                            {section.content.split('\n\n').map((p, i) => (
                              <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
                            ))}

                            <div className="bg-forest text-cream rounded-2xl p-6 space-y-3">
                              {section.realities.map((r, i) => (
                                <p key={i} className="font-body text-cream font-medium text-center leading-relaxed">{r}</p>
                              ))}
                            </div>

                            <div className="bg-terracotta/10 rounded-xl p-6 border-l-4 border-terracotta">
                              <p className="text-forest font-body leading-relaxed mb-3">{section.insight}</p>
                              <p className="text-forest font-body font-medium leading-relaxed">{section.consequences}</p>
                            </div>
                          </div>
                        )}

                        {/* Type: cta */}
                        {section.type === 'cta' && (
                          <>
                            {selectedResource.detailed.relatedResource && (
                              <div className="mb-4">
                                <p className="font-body text-xs uppercase tracking-wide text-stone-400 mb-2">{selectedResource.detailed.relatedResource.label}</p>
                                <a
                                  href={selectedResource.detailed.relatedResource.to}
                                  className="block bg-[#f5efe4] border border-stone-200 rounded-2xl p-5 hover:border-forest/30 transition-colors group"
                                  data-testid="related-resource-link"
                                >
                                  <div className="flex items-center justify-between gap-4">
                                    <div>
                                      <h4 className="font-heading text-lg text-forest mb-1">{selectedResource.detailed.relatedResource.title}</h4>
                                      <p className="font-body text-sm text-stone-600 leading-relaxed">{selectedResource.detailed.relatedResource.blurb}</p>
                                    </div>
                                    <span className="text-forest text-2xl group-hover:translate-x-1 transition-transform flex-shrink-0">→</span>
                                  </div>
                                </a>
                              </div>
                            )}
                            <div className="bg-gradient-to-br from-forest to-forest-dark text-cream rounded-2xl p-8 text-center">
                              <Heart size={28} className="text-terracotta mx-auto mb-4" />
                              <p className="font-body text-cream text-lg leading-relaxed mb-6">{section.content}</p>
                              <a
                                href="mailto:heyjbennett@yahoo.ca"
                                className="inline-block bg-terracotta text-white px-8 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors"
                              >
                                Reach Out to Jill
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Download Option */}
                {selectedResource.downloadUrl && (
                  <div className="pt-4 border-t border-stone-200">
                    <a
                      href={selectedResource.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-all"
                    >
                      <Download size={18} />
                      Download Original PDF
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Newsletter Soft Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <NewsletterSignup 
            variant="prompt" 
            onClose={dismissPrompt} 
            source="wellness-resources" 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default WellnessResourcesPage;
